/**
 * Keyboard and gamepad button mappings for SmashParty input system.
 *
 * KeyboardEvent.code values are layout-independent physical-key identifiers.
 * Reference: https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/code
 * (MDN; not resolvable via Context7 — this is a browser runtime API, not an npm package)
 *
 * Gamepad button indices follow the W3C Standard Gamepad layout:
 *   0=A/Cross, 1=B/Circle, 2=X/Square, 3=Y/Triangle,
 *   4=LB/L1, 5=RB/R1, 6=LT/L2, 7=RT/R2,
 *   8=Select/Back/Share, 9=Start/Options,
 *   10=LS press, 11=RS press, 12-15=D-pad
 * Reference: https://www.w3.org/TR/gamepad/#dfn-standard-gamepad
 */

/**
 * Per-player keyboard layout entry.
 * P2 does NOT define prevItem/nextItem (spec §10 line 753: P2 keyboard `-`).
 * Those fields are optional so the type system enforces their absence at compile time.
 *
 * Note on KEY_MAP.p1.item vs KEY_MAP.p1.nextItem: spec §10 lines 744 + 753 both
 * assign `KeyE` for P1. This is intentional — `E` serves as the board-phase
 * item/action key AND the inventory "next item" hotkey simultaneously.
 */
export interface KeyMapEntry {
  readonly move: {
    readonly up: string;
    readonly down: string;
    readonly left: string;
    readonly right: string;
  };
  readonly action: string;
  readonly item: string;
  readonly prevItem?: string;
  readonly nextItem?: string;
}

export type KeyboardLayout = 'p1' | 'p2';

export const KEY_MAP: { readonly p1: KeyMapEntry; readonly p2: KeyMapEntry } = {
  p1: {
    move: { up: 'KeyW', down: 'KeyS', left: 'KeyA', right: 'KeyD' },
    action: 'Space',
    item: 'KeyE',
    prevItem: 'KeyQ',
    nextItem: 'KeyE',
  },
  p2: {
    move: { up: 'ArrowUp', down: 'ArrowDown', left: 'ArrowLeft', right: 'ArrowRight' },
    action: 'ShiftRight',
    item: 'ControlRight',
    // prevItem and nextItem intentionally absent for P2 (spec §10 line 753)
  },
} as const;

export interface GamepadButtonMap {
  /** Bottom face button — A on Xbox, Cross on PS. */
  readonly action: number;
  /** Right face button — B on Xbox, Circle on PS. Also used as item/use. */
  readonly item: number;
  /** Top face button — Y on Xbox, Triangle on PS. */
  readonly viewMap: number;
  /** Select / Back / Share button. */
  readonly scoreboard: number;
  /** Start / Options button. */
  readonly pauseLeave: number;
  /** Left bumper — LB on Xbox, L1 on PS. */
  readonly prevItem: number;
  /** Right bumper — RB on Xbox, R1 on PS. */
  readonly nextItem: number;
}

export const GAMEPAD_BUTTON_MAP: GamepadButtonMap = {
  action: 0,
  item: 1,
  viewMap: 3,
  scoreboard: 8,
  pauseLeave: 9,
  prevItem: 4,
  nextItem: 5,
} as const;
